import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import moment from 'moment';
import 'moment-timezone';

import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { CurrentTimezoneStateService } from 'src/app/shared/current-timezone-state.service';

import { SystemBannersEditComponent } from '../system-banners-edit/system-banners-edit.component';
import { SystemBannersListDataSource } from '../system-banners-list-datasource';

@Component({
  standalone: false,
  selector: 'app-system-banners-list',
  templateUrl: './system-banners-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./system-banners-list.component.scss']
})
export class SystemBannersListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'select',
    'reorder',
    'content',
    'style',
    'platforms',
    'status',
    'starts_at',
    'ends_at',
    'actions'
  ];

  dataSource: SystemBannersListDataSource = new SystemBannersListDataSource(
    this.app,
    this.api
  );

  /**
   * Currently selected status filter. Null = All.
   */
  statusFilter: string | null = null;

  /**
   * Status filter options mapped to tab indexes.
   */
  statusFilterOptions: Array<{ label: string; value: string | null }> = [
    { label: 'All', value: null },
    { label: 'Draft', value: 'draft' },
    { label: 'Published', value: 'published' },
    { label: 'Archived', value: 'archived' }
  ];

  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    private router: Router,
    private dialog: MatDialog,
    private cTmzState: CurrentTimezoneStateService,
    public utils: UtilsService
  ) {}

  ngOnInit(): void {
    // Only super admins (is_sitesoft_admin) may access this page.
    if (!this.app.user.is_sitesoft_admin) {
      this.utils.showToast(
        'You do not have permission to access system banners.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    this.dataSource.status = this.statusFilter;
    this.dataSource.getData(true);
  }

  ngAfterViewInit(): void {
    if (!this.app.user.is_sitesoft_admin) {
      return;
    }

    // Sync paginators.
    const paginatorTap = tap((paginator: any) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    if (this.paginator1) {
      this.paginator1.page.pipe(paginatorTap).subscribe();
    }
    if (this.paginator2) {
      this.paginator2.page.pipe(paginatorTap).subscribe();
    }

    // Reset paginator + re-request on sort change.
    if (this.sort) {
      this.sort.sortChange.subscribe(() => {
        if (this.paginator1) this.paginator1.pageIndex = 0;
        if (this.paginator2) this.paginator2.pageIndex = 0;
      });

      this.sort.sortChange
        .pipe(
          tap((sorter: any) => {
            this.dataSource.order_by = sorter['active'];
            this.dataSource.order = sorter['direction'] || 'asc';
            this.dataSource.getData(true);
          })
        )
        .subscribe();
    }
  }

  /**
   * Handle tab changes for the status filter.
   */
  onStatusFilterChange(index: number): void {
    const option = this.statusFilterOptions[index];
    if (!option) {
      return;
    }
    this.statusFilter = option.value;
    this.dataSource.status = option.value;
    this.dataSource.getData(true);
  }

  /**
   * Returns a plain-text, truncated preview of the banner's HTML content.
   */
  /**
   * Returns a plain-text, truncated preview of the banner's HTML content.
   * Strips tags, decodes HTML entities (e.g., `&nbsp;`, `&amp;`),
   * collapses whitespace, and truncates to `maxLength` with an ellipsis.
   */
  getContentPreview(content: string, maxLength: number = 60): string {
    if (!content) {
      return '';
    }
    // Strip HTML tags first so we don't inject markup into the textarea.
    const stripped: string = String(content).replace(/<[^>]*>/g, '');
    // Decode HTML entities by writing to a textarea and reading back its
    // text value. This handles &nbsp;, &amp;, &#39;, &lt;, etc.
    const decoder: HTMLTextAreaElement = document.createElement('textarea');
    decoder.innerHTML = stripped;
    const decoded: string = decoder.value;
    // Collapse all whitespace (including non-breaking spaces) to a single
    // space, then trim.
    const plain: string = decoded.replace(/\s+/g, ' ').trim();
    if (plain.length <= maxLength) {
      return plain;
    }
    return plain.slice(0, maxLength).trim() + '...';
  }

  /**
   * Return the enabled platforms list for display.
   */
  getEnabledPlatforms(
    banner: any
  ): Array<{ key: string; icon: string; label: string; enabled: boolean }> {
    return [
      { key: 'web', icon: 'laptop', label: 'Web', enabled: !!banner.show_web },
      {
        key: 'mobile',
        icon: 'phone_iphone',
        label: 'Mobile',
        enabled: !!banner.show_mobile
      },
      {
        key: 'kiosk',
        icon: 'tablet',
        label: 'Kiosk',
        enabled: !!banner.show_kiosk
      }
    ];
  }

  /**
   * Format a banner schedule timestamp for the list view.
   *
   * The API returns starts_at / ends_at as UTC ISO strings. The list
   * displays them in the banner's saved timezone (falling back to the
   * super admin's current timezone, then UTC) so the value the super
   * admin entered is what they see when they scan the list.
   *
   * Returns an empty string for null inputs so the template can fall
   * back to its placeholder.
   */
  formatScheduleDate(
    value: string | null,
    bannerTimezone: string | null
  ): string {
    if (!value) {
      return '';
    }
    const zone: string =
      bannerTimezone || this.cTmzState.getCurrentTimezone() || 'UTC';
    const formatted: string = moment
      .utc(value)
      .tz(zone)
      .format('M/D/YY, h:mm A');
    const offsetSuffix: string = moment.tz(zone).format('z');
    return offsetSuffix ? `${formatted} (${offsetSuffix})` : formatted;
  }

  /**
   * Open the edit dialog. When banner is null, it opens in create mode.
   */
  onAdd(): void {
    this.openEditDialog(null);
  }

  onEdit(banner: any): void {
    this.openEditDialog(banner);
  }

  private openEditDialog(banner: any | null): void {
    this.utils
      .showComponentDialog(
        SystemBannersEditComponent,
        { banner: banner },
        { width: '800px', disableClose: true }
      )
      .then((result: any) => {
        if (result) {
          this.dataSource.getData();
        }
      });
  }

  onPublish(banner: any): void {
    this.utils.showModal(
      'Publish Banner',
      'Are you sure you want to publish this banner? It will become visible to users on the selected platforms.',
      () => {
        this.api
          .makeRequest(
            'post',
            `v2/admin/system-banners/${banner.id}/publish`,
            {}
          )
          .then(() => {
            this.utils.showToast('Banner published.');
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  onArchive(banner: any): void {
    this.utils.showModal(
      'Archive Banner',
      'Are you sure you want to archive this banner? It will no longer be visible to users.',
      () => {
        this.api
          .makeRequest(
            'post',
            `v2/admin/system-banners/${banner.id}/archive`,
            {}
          )
          .then(() => {
            this.utils.showToast('Banner archived.');
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  onRestore(banner: any): void {
    this.utils.showModal(
      'Restore Banner',
      'Are you sure you want to restore this banner? It will return to draft status.',
      () => {
        this.api
          .makeRequest(
            'post',
            `v2/admin/system-banners/${banner.id}/restore`,
            {}
          )
          .then(() => {
            this.utils.showToast('Banner restored to draft.');
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  onDelete(banner: any): void {
    this.utils.showModal(
      'Delete Banner',
      'Are you sure you want to delete this banner? This action cannot be undone.',
      () => {
        this.api
          .makeRequest('delete', `v2/admin/system-banners/${banner.id}`)
          .then(() => {
            this.utils.showToast('Banner deleted.');
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Move the banner at the given index up by one position and persist the new order.
   */
  onMoveUp(index: number): void {
    if (index <= 0) {
      return;
    }
    this.swapAndReorder(index, index - 1);
  }

  /**
   * Move the banner at the given index down by one position and persist the new order.
   */
  onMoveDown(index: number): void {
    const rows: any[] = this.dataSource.dataSubject.getValue() || [];
    if (index < 0 || index >= rows.length - 1) {
      return;
    }
    this.swapAndReorder(index, index + 1);
  }

  /**
   * Whether the banner at the given index is the last in the current list. Used to
   * disable the "move down" button on the last row.
   */
  isLastRow(index: number): boolean {
    const rows: any[] = this.dataSource.dataSubject.getValue() || [];
    return index >= rows.length - 1;
  }

  private swapAndReorder(fromIndex: number, toIndex: number): void {
    const rows: any[] = (this.dataSource.dataSubject.getValue() || []).slice();
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= rows.length ||
      toIndex >= rows.length
    ) {
      return;
    }

    // Swap the rows.
    const tmp = rows[fromIndex];
    rows[fromIndex] = rows[toIndex];
    rows[toIndex] = tmp;

    const ordered_ids: number[] = rows.map((row: any) => row.id);

    this.api
      .makeRequest('put', 'v2/admin/system-banners/reorder', {
        ordered_ids: ordered_ids
      })
      .then(() => {
        this.utils.showToast('Banner order updated.');
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * True when every banner currently on the visible page is selected.
   * Used to drive the header checkbox checked + indeterminate states.
   */
  isAllPageSelected(): boolean {
    const rows: any[] = this.dataSource.dataSubject.getValue() || [];
    if (rows.length === 0) {
      return false;
    }
    return rows.every((row) => this.dataSource.selection.isSelected(row.id));
  }

  /**
   * Bulk publish: send the selected ids to the bulk-publish endpoint.
   * The server skips banners that are not in the right status, so a mixed
   * selection still publishes whatever drafts are in it.
   */
  onBulkPublish(): void {
    const ids: number[] = this.dataSource.selection.selected.slice();
    if (ids.length === 0) {
      return;
    }
    this.utils.showModal(
      'Publish Selected Banners',
      'Are you sure you want to publish the ' +
        ids.length +
        ' selected ' +
        (ids.length === 1 ? 'banner' : 'banners') +
        '? ' +
        'Banners not currently in draft status will be skipped.',
      () => {
        this.api
          .makeRequest('post', 'v2/admin/system-banners/bulk-publish', {
            ids: ids
          })
          .then((response: any) => {
            const changed: number =
              response && response.meta
                ? Number(response.meta.changed || 0)
                : 0;
            this.utils.showToast(
              changed +
                ' ' +
                (changed === 1 ? 'banner' : 'banners') +
                ' published.'
            );
            this.dataSource.selection.clear();
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Bulk archive: send the selected ids to the bulk-archive endpoint.
   * Banners not in published status are silently skipped server-side.
   */
  onBulkArchive(): void {
    const ids: number[] = this.dataSource.selection.selected.slice();
    if (ids.length === 0) {
      return;
    }
    this.utils.showModal(
      'Archive Selected Banners',
      'Are you sure you want to archive the ' +
        ids.length +
        ' selected ' +
        (ids.length === 1 ? 'banner' : 'banners') +
        '? ' +
        'Banners not currently in published status will be skipped.',
      () => {
        this.api
          .makeRequest('post', 'v2/admin/system-banners/bulk-archive', {
            ids: ids
          })
          .then((response: any) => {
            const changed: number =
              response && response.meta
                ? Number(response.meta.changed || 0)
                : 0;
            this.utils.showToast(
              changed +
                ' ' +
                (changed === 1 ? 'banner' : 'banners') +
                ' archived.'
            );
            this.dataSource.selection.clear();
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Bulk restore: send the selected ids to the bulk-restore endpoint.
   * Banners not in archived status are silently skipped server-side.
   */
  onBulkRestore(): void {
    const ids: number[] = this.dataSource.selection.selected.slice();
    if (ids.length === 0) {
      return;
    }
    this.utils.showModal(
      'Restore Selected Banners',
      'Are you sure you want to restore the ' +
        ids.length +
        ' selected ' +
        (ids.length === 1 ? 'banner' : 'banners') +
        ' to draft? ' +
        'Banners not currently in archived status will be skipped.',
      () => {
        this.api
          .makeRequest('post', 'v2/admin/system-banners/bulk-restore', {
            ids: ids
          })
          .then((response: any) => {
            const changed: number =
              response && response.meta
                ? Number(response.meta.changed || 0)
                : 0;
            this.utils.showToast(
              changed +
                ' ' +
                (changed === 1 ? 'banner' : 'banners') +
                ' restored to draft.'
            );
            this.dataSource.selection.clear();
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Bulk delete: soft-delete the selected banners.
   */
  onBulkDelete(): void {
    const ids: number[] = this.dataSource.selection.selected.slice();
    if (ids.length === 0) {
      return;
    }
    this.utils.showModal(
      'Delete Selected Banners',
      'Are you sure you want to delete the ' +
        ids.length +
        ' selected ' +
        (ids.length === 1 ? 'banner' : 'banners') +
        '? ' +
        'This action cannot be undone from the UI.',
      () => {
        this.api
          .makeRequest('post', 'v2/admin/system-banners/bulk-delete', {
            ids: ids
          })
          .then((response: any) => {
            const deleted: number =
              response && response.meta
                ? Number(response.meta.deleted || 0)
                : 0;
            this.utils.showToast(
              deleted +
                ' ' +
                (deleted === 1 ? 'banner' : 'banners') +
                ' deleted.'
            );
            this.dataSource.selection.clear();
            this.dataSource.getData();
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }
}
